<template>
  <div>
    <div class="top-menu" :class="[store.getters.isLoggedIn ? 'login' : '']">
      <header class="d-flex justify-content-between">
        <div v-if="isGameWrapperRoute" class="header-tournament" @click.prevent="toggleTournaments()">
          <div class="icon">
            <img :src="`/img/tournaments/icons/${headerTournamentType}.png`">
            <div class="place" v-if="store.getters.getUser?.currentTournamentParticipant">
              <span>
                {{store.getters.getUser?.currentTournamentParticipant.place}}
              </span>
            </div>
          </div>
          <div class="info" v-if="store.getters.getUser?.currentTournamentParticipant">
            <div class="d-flex justify-content-between gap-xs">
              <div class="reward">
                <img src="/img/coins/gold_coin.svg">
                <span>{{numberFormatter.formatNumber(store.getters.getUser?.currentTournamentParticipant.prize)}}</span>
              </div>
            </div>
            <span class="duration" v-html="durationLabel"></span>
          </div>
          <div class="info" v-else>
            <div class="name">{{ $t(`tournament_short_names.${headerTournamentType}`) }}</div>
            <span class="duration" v-html="durationLabel"></span>
          </div>
        </div>
        <router-link v-else :to="{name:'home'}" style="align-self: center">
          <img alt="logo" src="/img/logo.png" style="height: 30px;">
        </router-link>
        <div v-if="store.getters.isLoggedIn" class="balance-wrapper">
          <div class="balance">
            <img src="/img/coins/gold_coin.svg">
            <span class="amount hidden">{{ numberFormatter.formatNumber(displayValue)?.replaceAll(/\d/g, '0') }}</span>
            <span class="amount">{{ numberFormatter.formatNumber(displayValue)}}</span>
          </div>
          <router-link :to="{name:'store'}" class="btn btn-primary text-capitalize-first">{{ $t('buy') }}</router-link>
        </div>
        <router-link v-else :to="{name:'login'}" class="btn btn-primary text-capitalize-first">{{ $t('login') }}</router-link>
      </header>
    </div>
    <TournamentSidebar v-if="isTournamentSidebarVisible">
    </TournamentSidebar>

  </div>
</template>
<style scoped>
.header-tournament {
  cursor: pointer;
  display: flex;
  background: #FFFFFF1F;
  padding: var(--margin-xxs);
  border-radius: var(--border-radius-sm);
  gap: var(--margin-xxs);
  align-items: center;
  padding-right: var(--margin-normal);
  .icon {
    background: #2A8EF81A;
    border-radius: var(--border-radius-xs);
    padding: var(--margin-xxs);
    width: 32px;
    display: flex;
    justify-content: center;
    position: relative;
    img {
      height: 24px;
    }

    .place {
      font-family: "Space Grotesk", sans-serif;
      font-weight: 700;
      font-size: 12px;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      right: 0;
      text-align: center;

      display: flex;
      align-items: center;
      justify-content: center;
      span {
        background: #00000087;
        border-radius: 12px;
        padding-right: 6px;
        padding-left: 6px;
      }
    }
  }

  .info {
    display: flex;
    flex-direction: column;

    .reward {
      font-family: "Space Grotesk", sans-serif;
      font-weight: 700;
      font-size: 12px;
      line-height: 20px;
      letter-spacing: 0%;
      display: flex;
      gap: var(--margin-xxs);
      align-items: center;
      img {
        width: 14px;
        height: 14px;
      }
    }
    .name {
      font-weight: 700;
      font-size: var(--text-size-small);
      line-height: 16px;
      letter-spacing: 0.5%;
    }
    .duration {
      font-weight: 500;
      font-size: 10px;
      line-height: 14px;
      letter-spacing: 0%;
      color: #FFFFFFA3;

      .highlight {
        color: #C4F82A;
      }
    }

  }
}
</style>

<script setup lang="ts">
import {useStore} from "vuex";
import {numberFormatter} from "@/service/NumberFormatter";
import {computed, onMounted, ref, watch} from "vue";
import {Currency} from "@/models/enum/Currency";
import {useRoute} from "vue-router";
import {reactive} from "@vue/reactivity";
import TournamentSidebar from "@/components/TournamentSidebar.vue";
import UserDTO from "@/models/UserDTO";
import TournamentDTO from "@/models/TournamentDTO";
import {tournamentName} from "@/models/enum/TournamentType";
import {startInLabelText, timeLeftLabelText} from "@/service/DateHelper";
import {TournamentStatus} from "@/models/enum/TournamentStatus";
const route = useRoute()
const store = useStore()

const state = reactive({
  isTournamentsVisible: false as boolean
})

const balance = computed(() => {
  return store.getters.getBalance(Currency.GOLD_COIN)
})

const displayValue = ref(balance.value);

const animateValue = (from: number, to: number, duration = 800) => {
  const start = performance.now();

  function frame(now: number) {
    const progress = Math.min((now - start) / duration, 1);
    const value = from + (to - from) * progress;

    displayValue.value = Math.floor(value).toString()
    if (progress < 1) {
      requestAnimationFrame(frame);
    }
  }
  requestAnimationFrame(frame);
}

watch(() => balance.value, async (newValue, oldValue) => {
  const oldBalance = Number.parseFloat(oldValue);
  const newBalance = Number.parseFloat(newValue);
  if (newBalance > oldBalance) {
    animateValue(oldBalance, newBalance);
  } else {
    displayValue.value = newBalance.toString()
  }
})

const headerTournament = computed((): TournamentDTO => {
  const user: UserDTO|null = store.getters.getUser

  const tournament = store.getters.liveTournaments.find((item: TournamentDTO) => item.id === user?.currentTournamentParticipant?.tournamentId)
  if (!tournament) {
    return store.getters.liveTournaments[0]
  }

  return tournament;
})

const durationLabel = computed(() => {
  return headerTournament.value.status === TournamentStatus.ACTIVE
    ? timeLeftLabelText(headerTournament.value.endAt)
    : startInLabelText(headerTournament.value.startAt)
})

const headerTournamentType = computed(() => {
  if (!headerTournament.value) {
    return null;
  }

  return tournamentName(headerTournament.value?.type)
})

const isGameWrapperRoute = computed(() => {
  return route.name === 'gameWrapper';
})

const isTournamentSidebarVisible = computed(() => {
  return isGameWrapperRoute.value && state.isTournamentsVisible
})

const toggleTournaments = () => {
  state.isTournamentsVisible = !state.isTournamentsVisible
}

</script>